import React from 'react'
import { Loader, LoaderProps } from '../loader'
import { twJoin } from 'tailwind-merge'
import { ButtonSizeEnum, ButtonTypeEnum } from './enums'
import Skeleton from 'react-loading-skeleton'

export interface CommonButtonProps
  extends Omit<
    React.AllHTMLAttributes<HTMLElement>,
    'type' | 'size' | 'children'
  > {
  loaderProps?: LoaderProps
  isLoading?: boolean
  hasSkeleton?: boolean
  size?: ButtonSizeEnum
  type?: ButtonTypeEnum
  label?: string
  children?: React.ReactNode
  iconBefore?: React.ReactNode
  iconAfter?: React.ReactNode
  icon?: React.ReactNode
  active?: boolean
}

const getSizeClasses = (size: ButtonSizeEnum, type: ButtonTypeEnum) => {
  if (type === ButtonTypeEnum.Text) return ''
  return size === ButtonSizeEnum.Small
    ? 'min-w-8 min-h-8'
    : size === ButtonSizeEnum.Medium
      ? 'min-w-10 min-h-10'
      : 'min-w-12 min-h-12'
}

const getIconSizeClasses = (size: ButtonSizeEnum) => {
  return size === ButtonSizeEnum.Small
    ? '[&>svg]:w-4 [&>svg]:h-4'
    : '[&>svg]:w-5 [&>svg]:h-5'
}

const getFontSize = (size: ButtonSizeEnum) => {
  return size === ButtonSizeEnum.Large ? 'text-L' : 'text-M'
}

const getPadding = (
  size: ButtonSizeEnum,
  type: ButtonTypeEnum,
  isIconButton: boolean,
) => {
  if (type === ButtonTypeEnum.Text) return 'p-0'

  const paddings = {
    [ButtonSizeEnum.Small]: isIconButton ? 'p-1.5' : 'px-3 py-1.5',
    [ButtonSizeEnum.Medium]: isIconButton ? 'p-2.5' : 'px-4 py-2.5',
    [ButtonSizeEnum.Large]: isIconButton ? 'p-3' : 'px-6 py-3',
  }

  return paddings[size]
}

const getTypeClasses = (type: ButtonTypeEnum, active: boolean) => {
  const base = {
    [ButtonTypeEnum.Primary]: twJoin(
      'text-textOnColor disabled:text-textDisabled',
      'bg-btnPrimary hover:bg-btnPrimaryHover active:bg-btnPrimaryPressed',
      'disabled:bg-btnDisabled disabled:hover:bg-btnDisabled',
      '[&>svg>path]:stroke-textOnColor disabled:[&>svg>path]:stroke-textDisabled',
      '[&>span]:border-textOnColor disabled:[&>span]:border-textDisabled',
      active && 'bg-btnPrimaryPressed',
    ),
    [ButtonTypeEnum.Secondary]: twJoin(
      'text-textPrimary disabled:text-textDisabled',
      'bg-btnSecondary hover:bg-btnSecondaryHover active:bg-btnSecondaryPressed',
      'disabled:bg-btnSecondaryDisabled disabled:hover:bg-btnSecondary',
      'border-[1px] border-solid!',
      'border-btnSecondaryBorder disabled:border-borderDisabled',
      '[&>svg>path]:stroke-iconSecondary disabled:[&>svg>path]:stroke-iconDisabled',
      '[&>span]:border-iconSecondary disabled:[&>span]:border-iconDisabled',
      active && 'bg-btnSecondaryPressed',
    ),
    [ButtonTypeEnum.Tertiary]: twJoin(
      'text-primary-500 hover:text-primary-600 disabled:text-textDisabled',
      'bg-btnTertiary hover:bg-btnTertiaryHover active:bg-btnTertiaryPressed',
      'disabled:bg-btnDisabled disabled:hover:bg-btnDisabled',
      '[&>svg>path]:stroke-primary-500 active:[&>svg>path]:stroke-primary-700',
      'hover:[&>svg>path]:stroke-primary-600 disabled:[&>svg>path]:stroke-textDisabled',
      '[&>span]:border-primary-500 active:[&>span]:border-primary-700',
      'hover:[&>span]:border-primary-600 disabled:[&>span]:border-textDisabled',
      active &&
        twJoin(
          'bg-btnTertiaryPressed text-primary-700 [&>svg>path]:stroke-primary-700',
          '[&>span]:border-primary-700',
        ),
    ),
    [ButtonTypeEnum.Ghost]: twJoin(
      'text-textPrimary disabled:text-textDisabled',
      'hover:bg-btnGhostPrimaryHover active:bg-btnGhostPrimaryPressed',
      'disabled:hover:bg-transparent',
      '[&>svg>path]:stroke-iconSecondary disabled:[&>svg>path]:stroke-iconDisabled',
      '[&>span]:border-iconSecondary disabled:[&>span]:border-iconDisabled',
      active && 'bg-btnGhostPrimaryPressed',
    ),
    [ButtonTypeEnum.Gray]: twJoin(
      'text-textPrimary bg-btnSecondaryHover disabled:text-iconDisabled disabled:bg-btnDisabled',
      'hover:bg-gray-100 active:bg-gray-150',
      'disabled:hover:bg-btnDisabled',
      '[&>svg>path]:stroke-iconSecondary disabled:[&>svg>path]:stroke-iconDisabled',
      '[&>span]:border-iconSecondary disabled:[&>span]:border-iconDisabled',
      active && 'bg-gray-150',
    ),
    [ButtonTypeEnum.Text]: twJoin(
      'text-textLink hover:text-textLinkHover disabled:text-textDisabled',
      'disabled:hover:text-textDisabled',
      'bg-bgPrimaryBase',
      '[&>svg>path]:stroke-textLink active:[&>svg>path]:stroke-textLinkPressed',
      'hover:[&>svg>path]:stroke-textLinkHover disabled:[&>svg>path]:stroke-textDisabled',
      '[&>span]:border-textLink active:[&>span]:border-textLinkPressed',
      'hover:[&>span]:border-textLinkHover disabled:[&>span]:border-textDisabled',
      active &&
        twJoin(
          'text-textLinkPressed [&>span]:border-textLinkPressed',
          '[&>svg>path]:stroke-textLinkPressed',
        ),
    ),
  }

  return base[type] ?? ''
}

export const CommonButton = ({
  className,
  isLoading = false,
  hasSkeleton = false,
  loaderProps = {},
  size = ButtonSizeEnum.Small,
  type = ButtonTypeEnum.Primary,
  iconBefore,
  iconAfter,
  icon,
  children = '',
  onClick,
  active = false,
  disabled = false,
  ...props
}: CommonButtonProps) => {
  const isIconButton = !!icon

  const classNames = twJoin(
    'relative transition-all duration-150',
    'flex flex-row items-center justify-center gap-1.5 rounded-md disabled:cursor-default',
    '[&>span]:border-t-transparent hover:[&>span]:border-t-transparent',
    'disabled:[&>span]:border-t-transparent',
    isLoading ? 'cursor-default' : 'cursor-pointer',
    getSizeClasses(size, type),
    getIconSizeClasses(size),
    getFontSize(size),
    getPadding(size, type, isIconButton),
    getTypeClasses(type, active),
    className,
  )

  return (
    <button
      type="button"
      className={classNames}
      onClick={!isLoading ? onClick : undefined}
      disabled={hasSkeleton || disabled}
      {...props}
    >
      {hasSkeleton && (
        <Skeleton
          containerClassName="absolute top-0 left-0 w-full h-full"
          className={twJoin(
            'h-full w-full',
            size === ButtonSizeEnum.Large ? 'leading-6!' : 'leading-5!',
          )}
        />
      )}
      {!isLoading && icon}
      {!isLoading && iconBefore}
      {isLoading && <Loader {...loaderProps} />}
      {children}
      {!isLoading && iconAfter}
    </button>
  )
}
